/**
 * AP-PORTAL-01: Supplier Portal Dashboard
 * Route: /portal/dashboard (protected)
 *
 * Shows supplier's ISSUED/CLOSED/CANCELLED POs with status filter,
 * acknowledgement indicators, and navigation to PO detail.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Package,
  CheckCircle2,
  AlertTriangle,
  LogOut,
  RefreshCw,
  ChevronRight,
  Archive,
  XCircle,
  Clock,
  Building2,
} from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ISSUED: {
    label: "Issued",
    bg: "#E8F5E9",
    text: "#2E7D32",
    border: "#A5D6A7",
    icon: Package,
  },
  CLOSED: {
    label: "Closed",
    bg: "#F5F5F5",
    text: "#616161",
    border: "#E0E0E0",
    icon: Archive,
  },
  CANCELLED: {
    label: "Cancelled",
    bg: "#FFEBEE",
    text: "#C62828",
    border: "#FFCDD2",
    icon: XCircle,
  },
};

function formatAmount(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD" };
  const Icon = cfg.icon || FileText;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.text,
        border: `1px solid ${cfg.border}`,
        letterSpacing: "0.3px",
      }}
    >
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

export default function SupplierPortalDashboard() {
  const { portalUser, setPortalUser } = usePortalUser();
  const navigate = useNavigate();

  const [pos, setPos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loggingOut, setLoggingOut] = useState(false);

  // Stats
  const [stats, setStats] = useState({ issued: 0, pending_ack: 0, exceptions: 0 });

  const loadPOs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 15 };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/api/portal/po`, {
        params,
        withCredentials: true,
      });
      setPos(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);

      // Compute stats from full ISSUED list (first page sans filter)
      if (!statusFilter && page === 1) {
        const issued = (res.data.items || []).filter((p) => p.status === "ISSUED");
        const pendingAck = issued.filter((p) => !p.acknowledged).length;
        const exceptions = (res.data.items || []).reduce((s, p) => s + (p.exception_count || 0), 0);
        setStats({ issued: issued.length, pending_ack: pendingAck, exceptions });
      }
    } catch {
      toast.error("Failed to load Purchase Orders");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadPOs();
  }, [loadPOs]);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await axios.post(`${API}/api/portal/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore — cookies cleared by browser
    }
    setPortalUser(null);
    navigate("/portal/login", { replace: true });
  }

  return (
    <div
      data-testid="supplier-portal-dashboard"
      style={{
        minHeight: "100vh",
        background: "#FBF7F2",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Nav */}
      <nav
        style={{
          background: "#1A1A1A",
          borderBottom: "1px solid rgba(201,161,95,0.2)",
          padding: "0 24px",
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "18px",
              fontWeight: 600,
              color: "#C9A15F",
            }}
          >
            Prashasta
          </span>
          <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
          <span style={{ fontSize: "12px", color: "#8A7A6A", letterSpacing: "1px", textTransform: "uppercase" }}>
            Partner Portal
          </span>
          <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
          <button
            onClick={() => navigate("/portal/dashboard")}
            style={{ fontSize: "13px", color: "#C9A15F", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
            data-testid="portal-nav-purchase-orders"
          >
            Purchase Orders
          </button>
          <button
            onClick={() => navigate("/portal/deliveries")}
            style={{ fontSize: "13px", color: "#C9A15F", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
            data-testid="portal-nav-deliveries"
          >
            My Deliveries
          </button>
          <button
            onClick={() => navigate("/portal/invoices")}
            style={{ fontSize: "13px", color: "#C9A15F", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
            data-testid="portal-nav-invoices"
          >
            My Invoices
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: "12px", color: "#F5EFE6", margin: 0, fontWeight: 500 }}>
              {portalUser?.name || portalUser?.email}
            </p>
            <p style={{ fontSize: "11px", color: "#6B5B4A", margin: 0 }}>
              {portalUser?.supplier_name || "Supplier"}
            </p>
          </div>
          <button
            data-testid="portal-logout-btn"
            onClick={handleLogout}
            disabled={loggingOut}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "7px 14px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "8px",
              color: "#8A7A6A",
              fontSize: "12px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <LogOut size={13} />
            {loggingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "28px" }}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "clamp(22px, 4vw, 30px)",
              fontWeight: 600,
              color: "#1A1A1A",
              margin: "0 0 4px",
            }}
          >
            Purchase Orders
          </h2>
          <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0 }}>
            View and manage your issued purchase orders from Prashasta Events
          </p>
        </div>

        {/* Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
            marginBottom: "28px",
          }}
        >
          <StatCard
            icon={<Package size={18} style={{ color: "#C9A15F" }} />}
            label="Active POs"
            value={stats.issued}
            testId="stat-active-pos"
          />
          <StatCard
            icon={<Clock size={18} style={{ color: "#D97706" }} />}
            label="Pending Acknowledgement"
            value={stats.pending_ack}
            testId="stat-pending-ack"
            accent={stats.pending_ack > 0 ? "#D97706" : undefined}
          />
          <StatCard
            icon={<AlertTriangle size={18} style={{ color: stats.exceptions > 0 ? "#DC2626" : "#9CA3AF" }} />}
            label="Open Exceptions"
            value={stats.exceptions}
            testId="stat-open-exceptions"
            accent={stats.exceptions > 0 ? "#DC2626" : undefined}
          />
          <StatCard
            icon={<FileText size={18} style={{ color: "#6366F1" }} />}
            label="My Invoices"
            value="→"
            testId="stat-my-invoices"
            onClick={() => navigate("/portal/invoices")}
          />
        </div>

        {/* Filters + refresh */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            {["", "ISSUED", "CLOSED", "CANCELLED"].map((s) => (
              <button
                key={s || "all"}
                data-testid={`filter-btn-${s || "all"}`}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  border: "1px solid",
                  transition: "all 0.15s",
                  background: statusFilter === s ? "#1A1A1A" : "transparent",
                  color: statusFilter === s ? "#F5EFE6" : "#6B5B4A",
                  borderColor: statusFilter === s ? "#1A1A1A" : "#D4C5B0",
                }}
              >
                {s || "All POs"}
              </button>
            ))}
          </div>
          <button
            data-testid="refresh-pos-btn"
            onClick={loadPOs}
            disabled={loading}
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              background: "transparent",
              border: "1px solid #D4C5B0",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#6B5B4A",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EDE5D8",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #EDE5D8",
                  borderTopColor: "#C9A15F",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  margin: "0 auto 12px",
                }}
              />
              <p style={{ color: "#9A8A78", fontSize: "13px" }}>Loading purchase orders…</p>
            </div>
          ) : pos.length === 0 ? (
            <div
              data-testid="no-pos-state"
              style={{ padding: "56px 24px", textAlign: "center" }}
            >
              <FileText size={36} style={{ color: "#D4C5B0", margin: "0 auto 12px" }} />
              <p style={{ color: "#6B5B4A", fontSize: "14px", fontWeight: 500 }}>No purchase orders found</p>
              <p style={{ color: "#9A8A78", fontSize: "13px" }}>
                {statusFilter
                  ? `No ${statusFilter.toLowerCase()} POs at this time`
                  : "Prashasta has not yet issued any POs for your account"}
              </p>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 1.2fr 1fr 80px 36px",
                  padding: "12px 20px",
                  background: "#FAF6F0",
                  borderBottom: "1px solid #EDE5D8",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#9A8A78",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                <span>PO Number</span>
                <span>Event</span>
                <span>Amount</span>
                <span>Issued</span>
                <span>Status</span>
                <span />
              </div>

              {/* Table rows */}
              {pos.map((po) => (
                <PORow
                  key={po.id}
                  po={po}
                  onClick={() => navigate(`/portal/po/${po.id}`)}
                />
              ))}

              {/* Pagination */}
              {pages > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 20px",
                    borderTop: "1px solid #EDE5D8",
                    fontSize: "12px",
                    color: "#9A8A78",
                  }}
                >
                  <span>
                    Showing {Math.min((page - 1) * 15 + 1, total)}–{Math.min(page * 15, total)} of {total}
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      data-testid="pagination-prev"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={paginationBtnStyle(page === 1)}
                    >
                      Previous
                    </button>
                    <button
                      data-testid="pagination-next"
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                      disabled={page === pages}
                      style={paginationBtnStyle(page === pages)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Upcoming features stub */}
        <div
          style={{
            marginTop: "40px",
            padding: "20px",
            background: "rgba(201,161,95,0.06)",
            border: "1px solid rgba(201,161,95,0.2)",
            borderRadius: "12px",
          }}
        >
          <p style={{ fontSize: "12px", fontWeight: 600, color: "#C9A15F", marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            Available Now
          </p>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            {[
              { label: "Delivery Evidence", to: "/portal/deliveries" },
              { label: "Invoice Submission", to: null },
            ].map((f) => (
              f.to ? (
                <button key={f.label} onClick={() => navigate(f.to)} style={{ fontSize: "13px", color: "#C9A15F", fontWeight: 500, display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#C9A15F", flexShrink: 0 }} />
                  {f.label}
                </button>
              ) : (
                <span key={f.label} style={{ fontSize: "13px", color: "#8A7A6A", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#D4C5B0", flexShrink: 0 }} />
                  {f.label} <span style={{ fontSize: "10px", color: "#B0A090" }}>(Coming Soon)</span>
                </span>
              )
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function StatCard({ icon, label, value, testId, accent, onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      style={{
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: "12px",
        padding: "18px 20px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        cursor: onClick ? "pointer" : "default",
        transition: onClick ? "box-shadow 0.15s" : "none",
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
      onMouseLeave={(e) => { if (onClick) e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          background: "#FAF6F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <p style={{ fontSize: "22px", fontWeight: 700, color: accent || "#1A1A1A", margin: "0 0 2px" }}>
          {value}
        </p>
        <p style={{ fontSize: "11px", color: "#9A8A78", margin: 0, lineHeight: 1.3 }}>{label}</p>
      </div>
    </div>
  );
}

function PORow({ po, onClick }) {
  const acknowledged = po.acknowledged;
  const hasExceptions = (po.exception_count || 0) > 0;

  return (
    <div
      data-testid={`po-row-${po.id}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{
        display: "grid",
        gridTemplateColumns: "1.5fr 1fr 1.2fr 1fr 80px 36px",
        padding: "14px 20px",
        borderBottom: "1px solid #F5F0E8",
        cursor: "pointer",
        transition: "background 0.12s",
        alignItems: "center",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6F0")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
            {po.po_number || po.internal_draft_ref || po.id.slice(0, 8)}
          </span>
          {!acknowledged && po.status === "ISSUED" && (
            <span
              style={{
                fontSize: "10px",
                fontWeight: 600,
                padding: "2px 6px",
                background: "#FEF3C7",
                color: "#92400E",
                border: "1px solid #FDE68A",
                borderRadius: "8px",
                letterSpacing: "0.3px",
              }}
            >
              ACK REQUIRED
            </span>
          )}
          {hasExceptions && (
            <AlertTriangle size={12} style={{ color: "#DC2626" }} />
          )}
        </div>
        {acknowledged && (
          <span style={{ fontSize: "11px", color: "#6B9E6B", display: "flex", alignItems: "center", gap: "3px", marginTop: "2px" }}>
            <CheckCircle2 size={10} /> Acknowledged
          </span>
        )}
      </div>
      <span style={{ fontSize: "12px", color: "#6B5B4A" }}>
        {po.event_name_snapshot || "—"}
      </span>
      <span style={{ fontSize: "13px", fontWeight: 500, color: "#1A1A1A" }}>
        {po.total_minor != null
          ? new Intl.NumberFormat("en-IN", { style: "currency", currency: po.currency_code || "INR", minimumFractionDigits: 0 }).format(po.total_minor / 100)
          : "—"}
      </span>
      <span style={{ fontSize: "12px", color: "#9A8A78" }}>
        {po.issued_at
          ? new Date(po.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
          : "—"}
      </span>
      <span>
        <StatusBadge status={po.status} />
      </span>
      <ChevronRight size={16} style={{ color: "#C9A15F", justifySelf: "end" }} />
    </div>
  );
}

function paginationBtnStyle(disabled) {
  return {
    padding: "5px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: disabled ? "not-allowed" : "pointer",
    background: disabled ? "#F5F0E8" : "#1A1A1A",
    color: disabled ? "#C4B39A" : "#F5EFE6",
    border: "none",
    opacity: disabled ? 0.5 : 1,
  };
}
